import mori from 'mori';
import composeFootnotes from './composeFootnotes';
import getActiveFootnotes from './getActiveFootnotes';
import getDistributions from './getDistributions';
import getLocations from './getLocations';
import getRelevantRows from './getRelevantRows';
import getTimeframes from './getTimeframes';
import labelFootnotes from './labelFootnotes';
import sortRows from './sortRows';

/**
 * Get footnotes for the current "view"
 *
 * If the tab is table, the footnotes need to be:
 *   #{year, #{parent-column, child-column}, location}
 *
 * If the tab is map or graph, the footnotes need to be:
 *   #{#{parent-column, child-column}, location}
 *
 * Parent-column or child-column can be null
 */
export default function getFootnotes(
  sheetData,
  options,
  filteredData,
  combinedFilters
) {
  const footnotes = mori.getIn(
    sheetData.config,
    ['footnotes', 'footnotes'],
    false
  );

  // If there are no footnotes, just return an empty hashMap
  if (!footnotes) {
    return mori.hashMap();
  }

  // Get the timeframes, locations, and distributions
  const timeframes = getTimeframes(filteredData);
  const locations = getLocations(combinedFilters[0]);
  const distributions = getDistributions(combinedFilters[1]);
  // Using the actives above, get the footnotes
  const activeFootnotes = getActiveFootnotes(
    footnotes,
    timeframes,
    locations,
    distributions
  );
  // Is a trend graph when there is more than two timeframes (timeframe & any)
  const isTrendGraph = mori.count(timeframes) > 2;
  // Pull out active locations (locations with footnotes);
  const activeLocations = mori.keys(activeFootnotes);
  // Pull out rows of data that have active locations
  const relevantRows = getRelevantRows(
    filteredData,
    activeLocations
  );
  // Sort the data, to match the table sort
  const sortedRows = mori.reduce(
    (acc, row) => mori.conj(
      acc,
      mori.hashMap(mori.first(row), mori.last(row))
    ),
    mori.hashMap(),
    sortRows(
      options.sortModel || {},
      relevantRows,
      sheetData.immutableHeaders,
      combinedFilters[3].type
    )
  );
  // Compose the footnotes over the sorted data
  let composedFootnotes = mori.reduce(
    (acc, item) => mori.conj(acc, item),
    mori.hashMap(),
    mori.filter(
      (note) => !locations || mori.some(
        (loc) => loc ===
          mori.first(mori.rest(mori.first(mori.first(note)))),
        locations
      ),
      composeFootnotes(footnotes, timeframes, distributions)
    )
  );

  if (isTrendGraph) {
    // If trendGraph view we need to do a secondary compose
    composedFootnotes = mori.reduceKV(
      (acc, key, val) => mori.conj(
        acc,
        mori.hashMap(
          (
            (mori.last(mori.rest(key)) !== 'Location') ?
              mori.set([
                mori.first(key),
                mori.first(mori.rest(key)),
                mori.hashMap(
                  mori.first(key),
                  mori.last(mori.rest(key))
                ),
              ]) :
              key
          ),
          val
        )
      ),
      mori.hashMap(),
      composedFootnotes
    );
  }
  // Go though and label the footnotes
  const labeledFootnotes = labelFootnotes(
    composedFootnotes,
    sortedRows,
    sheetData.immutableHeaders,
    timeframes
  );
  // Filter (dedup), the footnotes for other useage.
  const filteredFootnotes = mori.reduceKV(
    (acc, key, val) => mori.into(acc, val),
    mori.set(),
    labeledFootnotes
  );

  return {
    labeledFootnotes,
    filteredFootnotes,
  };
}



// WEBPACK FOOTER //
// ../datacenter/app/js/services/getFootnotes/index.js